import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

/**
 * RFL-160 — terminal device-log viewer/search. Reads the paginated
 * `GET /terminal-logs` surface (ingest + storage shipped in RFL-178). Since
 * RL-511 a range older than the Back Office window also reads the log history.
 */

export type TerminalLogRow = {
  id: string;
  terminalId: string;
  terminalCode: string;
  occurredAt: string;
  /** Not kept in the log history (RL-511): null for history rows. */
  receivedAt: string | null;
  level: string;
  tag: string | null;
  message: string;
  context: unknown;
  /** RL-511 — `recent`: Back Office's own copy; `history`: the long-term log store. */
  source: 'recent' | 'history';
};

/**
 * `recent`: numbered pages with a total (the range is inside the Back Office
 * window). `history` (RL-511): the range reaches older logs, paged newest
 * first by an opaque cursor, without a total.
 */
export type TerminalLogSearchResult =
  | {
      mode: 'recent';
      entries: TerminalLogRow[];
      page: number;
      pageSize: number;
      total: number;
    }
  | {
      mode: 'history';
      entries: TerminalLogRow[];
      pageSize: number;
      nextCursor: string | null;
      historyUnavailable: boolean;
      searchedBackTo: string;
    };

export type TerminalLogQuery = {
  terminalId?: string;
  level?: string;
  tag?: string;
  from?: string;
  to?: string;
  search?: string;
  page?: number;
  pageSize?: number;
  cursor?: string;
};

/** RL-511 — where the page's rows come from and how far back this tenant's history goes. */
export type TerminalLogSources = {
  recent: { days: number; from: string };
  history: {
    enabled: boolean;
    available: boolean;
    retentionDays: number | null;
    from: string | null;
  };
  /** RL-512 — the bounds of one export. */
  export: { maxRangeDays: number; maxRows: number; maxConcurrent: number };
};

export function useTerminalLogSources() {
  return useQuery({
    queryKey: ['terminal-logs', 'sources'] as const,
    queryFn: async (): Promise<TerminalLogSources> => {
      const res = await apiClient.get<TerminalLogSources>('/terminal-logs/sources');
      return res.data;
    },
    staleTime: 60_000,
  });
}

export function useTerminalLogs(query: TerminalLogQuery) {
  return useQuery({
    queryKey: ['terminal-logs', query] as const,
    queryFn: async (): Promise<TerminalLogSearchResult> => {
      const params: Record<string, string | number> = {};
      if (query.terminalId) params.terminalId = query.terminalId;
      if (query.level) params.level = query.level;
      if (query.tag) params.tag = query.tag;
      if (query.from) params.from = query.from;
      if (query.to) params.to = query.to;
      if (query.search) params.search = query.search;
      if (query.page !== undefined) params.page = query.page;
      if (query.pageSize !== undefined) params.pageSize = query.pageSize;
      if (query.cursor) params.cursor = query.cursor;
      // A history page may take several log-history reads (bounded server side
      // at ~20 s), longer than the client's default 15 s.
      const res = await apiClient.get<TerminalLogSearchResult>('/terminal-logs', {
        params,
        timeout: 30_000,
      });
      return res.data;
    },
  });
}

/** RL-512 — export formats: spreadsheet-safe CSV, or gzipped NDJSON. */
export type TerminalLogExportFormat = 'csv' | 'ndjson';

export type TerminalLogExportResult = {
  filename: string;
  /** The file ends with a truncation marker (row or time limit, history unreadable). */
  truncated: boolean;
};

/** How long the browser waits for an export (the server stops reading after 5 minutes). */
const EXPORT_TIMEOUT_MS = 6 * 60 * 1000;
/** The truncation marker is the file's last row/line; this much of the end is enough. */
const MARKER_TAIL_BYTES = 4096;

/** The last bytes of `stream`, as text. */
async function tailOf(stream: ReadableStream<Uint8Array>): Promise<string> {
  const reader = stream.getReader();
  let tail = new Uint8Array(0);
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    const joined = new Uint8Array(tail.length + value.length);
    joined.set(tail);
    joined.set(value, tail.length);
    tail = joined.slice(Math.max(0, joined.length - MARKER_TAIL_BYTES));
  }
  return new TextDecoder().decode(tail);
}

/**
 * Whether a downloaded export ends with the server's truncation marker. The
 * NDJSON file is gzipped, so it is inflated (streamed, only the end kept);
 * a browser without DecompressionStream cannot tell, and says no.
 */
export async function exportWasTruncated(
  blob: Blob,
  format: TerminalLogExportFormat,
): Promise<boolean> {
  if (format === 'csv') {
    const tail = await blob.slice(Math.max(0, blob.size - MARKER_TAIL_BYTES)).text();
    return /(^|\n)"# EXPORT TRUNCATED/.test(tail);
  }
  if (typeof DecompressionStream === 'undefined') return false;
  const tail = await tailOf(blob.stream().pipeThrough(new DecompressionStream('gzip')));
  return /(^|\n)\{"exportTruncated":/.test(tail);
}

/**
 * RL-512 — download the page's current filters and date range as a file.
 * The server streams it (CSV with a BOM for Excel, or NDJSON gzipped),
 * newest first, bounded; a truncated file ends with a marker row/line.
 */
export async function downloadTerminalLogExport(
  query: TerminalLogQuery,
  format: TerminalLogExportFormat,
): Promise<TerminalLogExportResult> {
  const params: Record<string, string> = { format };
  if (query.terminalId) params.terminalId = query.terminalId;
  if (query.level) params.level = query.level;
  if (query.tag) params.tag = query.tag;
  if (query.from) params.from = query.from;
  if (query.to) params.to = query.to;
  if (query.search) params.search = query.search;
  const res = await apiClient.get<Blob>('/terminal-logs/export', {
    params,
    responseType: 'blob',
    timeout: EXPORT_TIMEOUT_MS,
  });
  const disposition = String((res.headers as Record<string, unknown>)['content-disposition'] ?? '');
  const filename =
    /filename="([^"]+)"/.exec(disposition)?.[1] ??
    (format === 'csv' ? 'terminal-logs.csv' : 'terminal-logs.ndjson.gz');
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
  return { filename, truncated: await exportWasTruncated(res.data, format) };
}
